import React, { useState, useEffect, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import { ChevronDown, ChevronUp } from "react-ikonate";
import styles from "./Select.module.css";
import classNames from "classnames";

/**
 * Interactive dropdown select component with keyboard + mobile support
 */

export function Select({
  options,
  onChange,
  placeholder,
  disabled,
  dropdownRight = false,
  className,
  isScrollable,
  errors,
  isStateSelect,
}) {
  const [dropdownDisplay, setDropdownDisplay] = useState(false);
  const [selectedOption, setSelectedOption] = useState(placeholder);
  const [selectedIndex, setSelectedIndex] = useState(null);

  const pointerStartY = useRef(0);
  const pointerMoved = useRef(false);

  const wrapperRef = useRef(null);
  const dropdownRef = useRef(null);

  const allOptions = useMemo(
    () => (isStateSelect ? [placeholder, ...options] : options),
    [isStateSelect, options, placeholder]
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setDropdownDisplay(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Sync selected option with placeholder
  useEffect(() => {
    setSelectedOption(placeholder);
  }, [placeholder]);

  // Reset state when dropdown opens/closes
  useEffect(() => {
    if (!dropdownDisplay) {
      setSelectedIndex(null);
    } else {
      setSelectedIndex(0);
    }
  }, [dropdownDisplay]);

  // Scroll highlighted option into view
  useEffect(() => {
    if (dropdownDisplay && dropdownRef.current && selectedIndex !== null) {
      const el = dropdownRef.current.children[selectedIndex];
      if (el) {
        el.scrollIntoView({
          behavior: "instant",
          block: "nearest",
        });
      }
    }
  }, [selectedIndex, dropdownDisplay]);

  const handleKeyDown = (event) => {
    //  handle opening the dropdown
    if (!dropdownDisplay) {
      if (
        event.key === "Enter" ||
        event.key === " " ||
        event.key === "ArrowDown"
      ) {
        event.preventDefault();
        setDropdownDisplay(true);
        setSelectedIndex(0);
      }
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();

      if (selectedIndex !== null && allOptions[selectedIndex]) {
        handleOptionSelect(allOptions[selectedIndex]);
      }
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setSelectedIndex((prev) =>
        prev === null ? 0 : Math.min(prev + 1, allOptions.length - 1)
      );
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setSelectedIndex((prev) => (prev === null ? 0 : Math.max(prev - 1, 0)));
    }

    if (event.key === "Escape") {
      setDropdownDisplay(false);
    }

    // type-to-search
    if (event.key.length === 1) {
      const key = event.key.toLowerCase();

      const index = allOptions.findIndex((option) =>
        option.toLowerCase().startsWith(key)
      );

      if (index !== -1) {
        setSelectedIndex(index);
      }
    }
  };

  const handleOptionSelect = (option) => {
    setSelectedOption(option);
    onChange(option);
    setDropdownDisplay(false);
  };

  const handlePointerDown = (e) => {
    pointerStartY.current = e.clientY;
    pointerMoved.current = false;
  };

  const handlePointerMove = (e) => {
    const deltaY = Math.abs(e.clientY - pointerStartY.current);
    if (deltaY > 10) {
      pointerMoved.current = true;
    }
  };

  const handlePointerUp = (option, e) => {
    if (pointerMoved.current) return;

    e.preventDefault();
    handleOptionSelect(option);
  };

  return (
    <div
      ref={wrapperRef}
      className={`${styles.container} ${errors ? styles.errorBorder : ""}`}
    >
      <button
        type="button"
        className={classNames(styles.selectDropdown, className, {
          [styles.selectDropdownOpened]: dropdownDisplay,
        })}
        onClick={() => setDropdownDisplay((prev) => !prev)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
      >
        <div className={styles.selectField}>
          <span className={styles.placeholder}>{selectedOption}</span>
          <span className={styles.selectChevron}>
            <ChevronUp fontSize="1em" />
            <ChevronDown fontSize="1em" />
          </span>
        </div>
      </button>

      {dropdownDisplay && (
        <div
          ref={dropdownRef}
          className={classNames(
            dropdownRight ? styles.selectOptionsRight : styles.selectOptions,
            { [styles.scrollable]: isScrollable },
            errors ? styles.withError : ""
          )}
          style={{ width: wrapperRef.current?.offsetWidth }}
        >
          {(isStateSelect ? allOptions : options).map((option, index) => (
            <button
              type="button"
              key={option}
              className={classNames(
                styles.selectMenuOptions,
                selectedIndex === index ? styles.selected : ""
              )}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={(e) => handlePointerUp(option, e)}
            >
              {option}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

Select.propTypes = {
  options: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
};

Select.defaultProps = {
  disabled: false,
};

export default Select;
